import React from "react";
import { Loader2, LucideIcon } from "lucide-react";
import telegramService from "../../utils/telegram";

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "premium";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: LucideIcon;
  fullWidth?: boolean;
  /** false — haptic o'chirilgan; default variantga qarab tanlanadi */
  haptic?: Parameters<typeof telegramService.haptic>[0] | false;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "bg-gradient-to-br from-brand-500 to-coral-500 text-white shadow-soft",
  secondary: "bg-surface text-content-primary border border-line-strong shadow-card",
  ghost: "text-content-secondary hover:bg-inset",
  danger: "bg-danger text-white",
  premium:
    "bg-gradient-to-br from-indigo-500 via-purple-500 to-coral-500 text-white shadow-soft",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "px-4 py-2 text-xs rounded-xl gap-1.5",
  md: "px-5 py-3 text-sm rounded-2xl gap-2",
  lg: "px-6 py-3.5 text-sm rounded-2xl gap-2",
};

const ICON_SIZES: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18 };

const DEFAULT_HAPTIC: Record<ButtonVariant, Parameters<typeof telegramService.haptic>[0]> = {
  primary: "medium",
  secondary: "light",
  ghost: "light",
  danger: "medium",
  premium: "medium",
};

const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  size = "md",
  loading = false,
  icon: Icon,
  fullWidth = false,
  haptic,
  className = "",
  children,
  onClick,
  disabled,
  ...rest
}) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (haptic !== false) {
      telegramService.haptic(haptic ?? DEFAULT_HAPTIC[variant]);
    }
    onClick?.(e);
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center font-bold
        active:scale-[0.97] transition-all duration-150
        disabled:opacity-50 disabled:pointer-events-none
        ${VARIANT_CLASSES[variant]} ${SIZE_CLASSES[size]}
        ${fullWidth ? "w-full" : ""} ${className}`}
      {...rest}
    >
      {loading ? (
        <Loader2 size={ICON_SIZES[size]} className="animate-spin" />
      ) : (
        Icon && <Icon size={ICON_SIZES[size]} />
      )}
      {children}
    </button>
  );
};

export default Button;
